// src/features/system/admin/components/RbacManagement.tsx
import React, { useMemo, useState, useEffect } from 'react';
import Tabs from '@/shared/components/ui/Tabs';
import { useAuth } from '@/shared/auth';

import RolesTable       from '@/features/system/rbac/components/RolesTable';
import AbilitiesTable   from '@/features/system/rbac/components/AbilitiesTable';
import UserRolesTable   from '@/features/system/rbac/components/UserRolesTable';
import RoleServiceTab   from '@/features/system/rbac/components/RoleServiceTab';

type TabKey = 'roles' | 'abilities' | 'user_role' | 'role_service';

const COMPONENTS: Record<TabKey, React.ReactNode> = {
  roles: <RolesTable />,
  abilities: <AbilitiesTable />,
  user_role: <UserRolesTable />,
  role_service: <RoleServiceTab />,
};

const KNOWN_KEYS: readonly TabKey[] = ['roles','abilities','user_role','role_service'] as const;
const isTabKey = (k: string): k is TabKey => (KNOWN_KEYS as readonly string[]).includes(k);

export default function RbacManagement() {
  const auth = useAuth();
  const loading  = auth.loading;
  const rawPerms = auth.perms as unknown; // array or Set

  const permsSet: Set<string> | undefined = rawPerms instanceof Set ? rawPerms : undefined;
  const permsArr: string[] = Array.isArray(rawPerms) ? rawPerms : Array.from(permsSet ?? []);
  const has = (p: string) => (permsSet ? permsSet.has(p) : permsArr.includes(p));

  // Determine which RBAC tabs to show based on permissions
  const tabs = useMemo(() => {
    const out: { key: TabKey; label: string }[] = [];
    if (has('system.roles')) out.push({ key: 'roles', label: 'Roles' });
    if (has('system.roles_abilities')) out.push({ key: 'abilities', label: 'Abilities' });
    if (has('system.roles')) out.push({ key: 'user_role', label: 'User → Role' });
    if (has('system.role_services')) out.push({ key: 'role_service', label: 'Role → Service' });
    return out;
  }, [permsSet, permsArr]);

  const [active, setActive] = useState<TabKey | null>(tabs[0]?.key ?? null);
  useEffect(() => {
    if (!active || !tabs.find(t => t.key === active)) setActive(tabs[0]?.key ?? null);
  }, [tabs, active]);

  if (loading) return <section className="bg-neutral-900 rounded-lg p-4">Loading…</section>;
  if (!tabs.length) return (
    <section className="bg-neutral-900 rounded-lg p-4">
      <p className="text-neutral-400">You don’t have access to RBAC Management.</p>
    </section>
  );

  return (
    <section className="bg-neutral-900 rounded-lg p-4 space-y-6">
      <Tabs
        tabs={tabs.map(t => t.label)}
        activeTab={tabs.find(t => t.key === active)?.label ?? tabs[0].label}
        onTabChange={(label) => {
          const found = tabs.find(t => t.label === label);
          if (found) setActive(found.key);
        }}
      />
      {active && COMPONENTS[active]}
    </section>
  );
}
